import { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES } from "@/contants";
import {
  formatCorrectScoresForGrid,
  splitCorrectScoresListToSections,
} from "@/utils/matchInfo";

import ICON_LEAGUE from "@/assets/images/cumeo-league.svg";
import CumeoMatchMinute from "../CumeoMatchMinute";
import CumeoOddsItem from "../CumeoOddsItem";
import CuMeoOtherBetsDropdown from "../CuMeoOtherBetsDropdown/CuMeoOtherBetsDropdown";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function CumeoMatch({ match }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const { getMatchOdds } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const { currentHomeScore, currentAwayScore } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;
  const leagueName =
    match?.league?.name || match?.leagueName || match?.league?.league_name;
  const leagueLogo =
    match?.league?.logo ||
    match?.leagueLogo ||
    match?.league_logo ||
    match?.league?.league_logo;

  // Data processing for Other Beths Dropdown 
  const correctScoresListFull = otherOddsData?.correctScoresOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
    : [];
  const correctScoresFull = splitCorrectScoresListToSections(
    correctScoresListFull,
  );
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds)
    ? otherOddsData.totalCornersOdds
    : [];
  const hasOtherData =
    otherOddsData?.goalOddEvenOdds?.length > 0 ||
    correctScoresListFull?.length > 0 ||
    totalCornersOdds?.length > 0;

  return (
    <div className="flex flex-col overflow-hidden m-0 p-0 border-l  border-r border-t-0   border-b-0 border-solid border-[var(--theme-border)] box-border bg-transparent">
      {/* DESKTOP */}
      <div className="mb-hidden md:grid grid-cols-[70px_155px_2.5fr_5fr] items-center px-2 py-3 gap-2 border-b border-0 border-solid border-[var(--theme-border)] last:border-b-0 hover:bg-[var(--theme-accent)] transition duration-200 group relative m-0 p-4 box-border cursor-pointer">
        <CumeoMatchMinute
          isLive={isLive}
          match={match}
          liveScore={liveScore}
          status={statusNum}
          matchTime={match?.matchTime}
          halfStartTime={match?.halfStartTime}
        />

        <div className="flex items-center space-x-1 overflow-hidden m-0 p-0 box-border">
          <div className="h-7 w-7 shrink-0 bg-white/5 rounded-full p-1 border border-solid border-[var(--theme-border)] m-0 p-0 box-border">
            <img
              src={leagueLogo || ""}
              alt=""
              className="object-contain w-full h-full m-0 p-0"
            />
          </div>
          <span className="text-sm font-bold text-[var(--theme-text-secondary)] line-clamp-2 leading-tight m-0 p-0">
            {leagueName}
          </span>
        </div>

        <div className="flex flex-col items-start gap-2 border-r border-0 border-solid border-[var(--theme-border)] pr-2 m-0 p-0">
          <div className="flex items-center justify-between w-full m-0 p-0">
            <div className="flex items-center space-x-3 overflow-hidden m-0 p-0">
              <div className="h-7 w-7 shrink-0 m-0 p-0">
                <img
                  src={homeLogo || ""}
                  alt=""
                  className="object-contain w-full h-full m-0 p-0"
                />
              </div>
              <span className="text-sm font-bold line-clamp-1 text-[var(--theme-text)] m-0 p-0">
                {match?.homeName}
              </span>
            </div>
            <div className={`text-lg font-bold pl-2 ${Number(currentHomeScore || 0) > Number(currentAwayScore || 0) ? "text-[var(--theme-primary)]" : "text-[var(--theme-highlight)]"} m-0 p-0`}>
              {currentHomeScore ?? "-"}
            </div>
          </div>
          <div className="flex items-center justify-between w-full m-0 p-0">
            <div className="flex items-center space-x-3 overflow-hidden m-0 p-0">
              <div className="h-7 w-7 shrink-0 m-0 p-0">
                <img
                  src={awayLogo || ""}
                  alt=""
                  className="object-contain w-full h-full m-0 p-0"
                />
              </div>
              <span className="text-sm font-bold line-clamp-1 text-[var(--theme-text)] m-0 p-0">
                {match?.awayName}
              </span>
            </div>
            <div className={`text-lg font-bold pl-2 ${Number(currentAwayScore || 0) > Number(currentHomeScore || 0) ? "text-[var(--theme-primary)]" : "text-[var(--theme-highlight)]"} m-0 p-0`}>
              {currentAwayScore ?? "-"}
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center gap-x-2 m-0 p-0">
          <div className="flex flex-col gap-y-1  shrink-0 text-[10px] font-bold text-[var(--text-half-score)] uppercase m-0 p-0">
            <div className="h-7 flex items-center justify-start pr-1 border-r border-0 border-solid border-[var(--theme-border)] m-0 text-[var(--theme-text)]">
              Toàn trận
            </div>
            <div className="h-7 flex items-center justify-start pr-1 border-r border-0 border-solid border-[var(--theme-border)] m-0 text-[var(--theme-text)]">
              Hiệp 1
            </div>
          </div>

          {/* Handicap  */}
          <div className="flex flex-col gap-1 flex-1 m-0 p-0">
            <CumeoOddsItem
              leftOdds={homeHandicapOdds}
              leftTrend={homeHandicapTrend}
              middleValue={handicapValue}
              rightOdds={awayHandicapOdds}
              rightTrend={awayHandicapTrend}
            />
            <CumeoOddsItem
              leftOdds={homeHandicapHalfOdds}
              leftTrend={homeHandicapHalfTrend}
              middleValue={handicapHalfValue}
              rightOdds={awayHandicapHalfOdds}
              rightTrend={awayHandicapHalfTrend}
              isHT={true}
            />
          </div>

          {/* Tài/Xỉu */}
          <div className="flex flex-col gap-1 flex-1 m-0 p-0">
            <CumeoOddsItem
              leftOdds={overOdds}
              leftTrend={overTrend}
              middleValue={overUnderValue}
              rightOdds={underOdds}
              rightTrend={underTrend}
            />
            <CumeoOddsItem
              leftOdds={overHalfOdds}
              leftTrend={overHalfTrend}
              middleValue={overUnderHalfValue}
              rightOdds={underHalfOdds}
              rightTrend={underHalfTrend}
              isHT={true}
            />
          </div>

          {/* 1X2  */}
          <div className="flex flex-col gap-1 flex-1 m-0 p-0">
            <CumeoOddsItem
              leftOdds={homeEuropeOdds}
              leftTrend={homeEuropeTrend}
              middleValue={instantDrawOdds}
              middleTrend={drawTrend}
              middleColor="text-[var(--theme-text)]"
              rightOdds={awayEuropeOdds}
              rightTrend={awayEuropeTrend}
            />
            <CumeoOddsItem
              leftOdds={homeEuropeHalfOdds}
              leftTrend={homeEuropeHalfTrend}
              middleValue={drawHalfOdds}
              middleTrend={drawHalfTrend}
              middleColor="text-[var(--theme-text)]"
              rightOdds={awayEuropeHalfOdds}
              rightTrend={awayEuropeHalfTrend}
              isHT={true}
            />
          </div>
        </div>

        {/* Desktop Other Bets Button */}
        <div className="col-span-4 flex justify-end mt-2 m-0 p-0 pt-2 border-t border-[var(--theme-border)]/50">
          <div
            onClick={handleOtherOddsClick}
            className="flex items-center justify-center px-2 py-1 uppercase font-bold text-[var(--theme-on-primary)] shadow-[inset_-2px_-4px_4px_0px_rgba(0,0,0,0.25)] cursor-pointer m-0 box-border rounded-sm"
            style={{
              background:
                "linear-gradient(91deg,var(--theme-gradient-start),var(--theme-gradient-end))",
              transform: "skewX(-8deg)",
            }}
          >
            <span
              style={{ transform: "skewX(8deg)" }}
              className="m-0 p-0 text-[var(--theme-on-primary)]   text-sm font-bold"
            >
              Kèo khác
            </span>
          </div>
        </div>
      </div>

      {/* MOBILE */}
      <div className="md:hidden grid grid-cols-12 items-center p-3 gap-y-3 gap-x-2 border-b border-0 border-solid border-[var(--theme-border)] last:border-b-0 hover:bg-[var(--theme-bg-hover)] transition m-0 box-border">
        <CumeoMatchMinute
          isLive={isLive}
          match={match}
          liveScore={liveScore}
          status={statusNum}
          matchTime={match?.matchTime}
          halfStartTime={match?.halfStartTime}
          isMobile={true}
          className="col-span-4 sm:col-span-3 min-w-0"
        />

        <div className="col-span-8 sm:col-span-9 flex flex-col items-end gap-2 m-0 p-0 min-w-0">
          <div className="flex items-center justify-end space-x-2 text-sm uppercase font-bold text-[var(--theme-text-secondary)] truncate w-full m-0 p-0">
            <img src={ICON_LEAGUE} alt="" className="w-4 h-4 shrink-0" />
            <span className="truncate m-0 p-0">{leagueName}</span>
          </div>
        </div>

        <div className="col-span-8 flex flex-col items-start gap-2 mt-2 m-0 p-0">
          <div className="flex items-center space-x-1 m-0 p-0">
            <div className={`text-base font-bold ${Number(currentHomeScore || 0) > Number(currentAwayScore || 0) ? "text-[var(--theme-primary)]" : "text-[var(--theme-highlight)]"} min-w-[10px] m-0 p-0`}>
              {currentHomeScore ?? "0"}
            </div>
            <div className="h-7 w-7 shrink-0 m-0 p-0">
              <img
                src={homeLogo || ""}
                alt=""
                className="object-contain w-full h-full"
              />
            </div>
            <span className="text-sm text-[var(--theme-text)] font-semibold truncate max-w-[160px]">
              {match?.homeName}
            </span>
          </div>
          <div className="flex items-center space-x-1 m-0 p-0">
            <div className={`text-base font-bold ${Number(currentAwayScore || 0) > Number(currentHomeScore || 0) ? "text-[var(--theme-primary)]" : "text-[var(--theme-highlight)]"} min-w-[10px] m-0 p-0`}>
              {currentAwayScore ?? "0"}
            </div>
            <div className="h-7 w-7 shrink-0 m-0 p-0">
              <img
                src={awayLogo || ""}
                alt=""
                className="object-contain w-full h-full"
              />
            </div>
            <span className="text-sm text-[var(--theme-text)] font-semibold truncate max-w-[160px]">
              {match?.awayName}
            </span>
          </div>
        </div>

        <div className={`col-span-4 flex flex-col items-end justify-end mt-2 m-0 p-0 ${!isLive ? "rounded-sm" : ""}`}>
          <div className="flex flex-col items-center gap-2 m-0 p-0">
            <span className={`flex items-center space-x-1 text-sm font-bold uppercase tracking-wider m-0 p-0 ${isLive ? "text-[var(--theme-highlight)]" : "text-gray-400"}`}>
              <span className={`inline-block w-2 h-2 rounded-full ${isLive ? "bg-[var(--theme-highlight)] animate-pulse" : "bg-gray-300"}`}></span>
              <span>Live</span>
            </span>
            <div
              onClick={handleOtherOddsClick}
              className="flex items-center justify-center px-2 py-1 uppercase font-bold text-[var(--theme-on-primary)] shadow-[inset_-2px_-4px_4px_0px_rgba(0,0,0,0.25)] cursor-pointer m-0 box-border rounded-sm"
              style={{
                background:
                  "linear-gradient(91deg,var(--theme-gradient-start),var(--theme-gradient-end))",
                transform: "skewX(-8deg)",
              }}
            >
              <span
                style={{ transform: "skewX(8deg)" }}
                className="m-0 p-0 text-[var(--theme-on-primary)]   text-sm font-bold"
              >
                Kèo khác
              </span>
            </div>
          </div>
        </div>

        <div className="col-span-12 mt-3 pt-3 border-t border-0 border-solid border-[var(--theme-border)] space-y-2 m-0 p-0 box-border">
          <div className="grid grid-cols-[40px_1fr_1fr_1fr] px-1 m-0 p-0">
            <div className="m-0 p-0"></div>
            <span className="text-[9px] text-center text-[var(--text-half-score)] uppercase font-bold m-0 p-0">
              HDP
            </span>
            <span className="text-[9px] text-center text-[var(--text-half-score)] uppercase font-bold m-0 p-0">
              O/U
            </span>
            <span className="text-[9px] text-center text-[var(--text-half-score)] uppercase font-bold m-0 p-0">
              1X2
            </span>
          </div>
          {/* FT Row */}
          <div className="grid grid-cols-[20px_1fr_1fr_1fr] items-center gap-2 m-0 p-0">
            <div className="text-[10px] font-bold text-[var(--theme-text-muted)] uppercase text-right pr-2 border-r border-0 border-solid border-[var(--theme-border)] m-0 p-0 flex items-center justify-end h-7">
              FT
            </div>
            <CumeoOddsItem
              leftOdds={homeHandicapOdds}
              leftTrend={homeHandicapTrend}
              middleValue={handicapValue}
              rightOdds={awayHandicapOdds}
              rightTrend={awayHandicapTrend}
              className="col-span-1"
            />
            <CumeoOddsItem
              leftOdds={overOdds}
              leftTrend={overTrend}
              middleValue={overUnderValue}
              rightOdds={underOdds}
              rightTrend={underTrend}
              className="col-span-1"
            />
            <CumeoOddsItem
              leftOdds={homeEuropeOdds}
              leftTrend={homeEuropeTrend}
              middleValue={instantDrawOdds}
              middleTrend={drawTrend}
              middleColor="text-[var(--theme-text)]"
              rightOdds={awayEuropeOdds}
              rightTrend={awayEuropeTrend}
              className="col-span-1"
            />
          </div>
          {/* HT Row */}
          <div className="grid grid-cols-[20px_1fr_1fr_1fr] items-center gap-2 m-0 p-0">
            <div className="text-[10px] font-bold text-[var(--theme-text-muted)] uppercase text-right pr-2 border-r border-0 border-solid border-[var(--theme-border)] m-0 p-0 flex items-center justify-end h-7">
              HT
            </div>
            <CumeoOddsItem
              leftOdds={homeHandicapHalfOdds}
              leftTrend={homeHandicapHalfTrend}
              middleValue={handicapHalfValue}
              rightOdds={awayHandicapHalfOdds}
              rightTrend={awayHandicapHalfTrend}
              isHT={true}
              className="col-span-1"
            />
            <CumeoOddsItem
              leftOdds={overHalfOdds}
              leftTrend={overHalfTrend}
              middleValue={overUnderHalfValue}
              rightOdds={underHalfOdds}
              rightTrend={underHalfTrend}
              isHT={true}
              className="col-span-1"
            />
            <CumeoOddsItem
              leftOdds={homeEuropeHalfOdds}
              leftTrend={homeEuropeHalfTrend}
              middleValue={drawHalfOdds}
              middleTrend={drawHalfTrend}
              middleColor="text-[var(--theme-text)]"
              rightOdds={awayEuropeHalfOdds}
              rightTrend={awayEuropeHalfTrend}
              isHT={true}
              className="col-span-1"
            />
          </div>
        </div>
      </div>

      {showOther && (
        <CuMeoOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{
            handicapValue,
            homeHandicapOdds,
            awayHandicapOdds,
            homeHandicapTrend,
            awayHandicapTrend,
            overUnderValue,
            overOdds,
            underOdds,
            overTrend,
            underTrend,
            homeEuropeOdds,
            awayEuropeOdds,
            instantDrawOdds,
            homeEuropeTrend,
            awayEuropeTrend,
            drawTrend,
            handicapHalfValue,
            homeHandicapHalfOdds,
            awayHandicapHalfOdds,
            homeHandicapHalfTrend,
            awayHandicapHalfTrend,
            overUnderHalfValue,
            overHalfOdds,
            underHalfOdds,
            overHalfTrend,
            underHalfTrend,
            homeEuropeHalfOdds,
            awayEuropeHalfOdds,
            drawHalfOdds,
            homeEuropeHalfTrend,
            awayEuropeHalfTrend,
            drawHalfTrend,
          }}
          showOther={showOther}
        />
      )}
    </div>
  );
}
